import type {Config} from '@/types'

export interface ResponseData {
	success: boolean
	data?: any
	error?: string
	response_code?: number
	response_text?: string
}

export class ApiService {

	private config: Config | null = null
	private accessTokens: Record<string, { access_token: string, expires_at: number }> = {}
	private authorization_expires_at: number = 0
	private user_id: string = ''

	constructor(config?: Config) {
		if (config)
			this.setConfig(config);
	}

	async fetchConfig(): Promise<Config> {

		// Получаем конфигурацию с сервера
		const response = await fetch('/config.json');
		if (!response.ok)
			throw new Error(`Ошибка загрузки конфига: ${response.status}`);

		const config = await response.json();

		// Сохраняем и возвращаем конфигурацию
		this.setConfig(config);
		return config;
	}

	setConfig(config: Config) {
		// Проверяем обязательные поля
		if (!this.checkConfig(config))
			throw new Error('Неверный формат конфигурационного файла');
		// Сохраняем конфигурацию
		this.config = config;
	}

	checkConfig(config?: Config): boolean {
		return (
			!!config && typeof config === 'object'
			&& !!config.base_domain
			&& !!config.cp_domain
			&& !!config.auth_domain
		);
	}

	haveConfig(): boolean {
		return !!this.config;
	}

	getConfig(): Config {
		if (!this.config)
			throw new Error('Конфигурация приложения отсутствует');
		return this.config;
	}

	async doFetch(
		domain: string,
		path: string,
		method: string = 'GET',
		body: any = undefined,
		headers?: Record<string, string>
	): Promise<Response> {
		// Получаем правильный domain, если он задан
		if (!!domain)
			domain = (new URL(
				domain.startsWith('http') ? domain : 'https://' + domain
			)).hostname;

		// Проверяем наличие пути запроса
		if (!path || !path.startsWith('/'))
			throw new Error('Переданный path не задан, либо не корректный');

		// Проверяем метод запроса
		const allowed_methods = this.getConfig().allowed_http_params?.methods || ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
		if (!allowed_methods.includes(method.toUpperCase()))
			throw new Error('Переданный method не поддерживается');

		// Определяем URL для запроса и получаем access_token, если домен указан
		let url: string;
		let access_token = '';

		// Если домен указан
		if (domain) {
			const scheme = this.resolveRequestScheme(domain);
			url = `${scheme}://${domain}${path}`;

			// Получаем access_token для домена, если это не authDomain
			if (domain !== this.getConfig().auth_domain) {
				access_token = (await this.getAccessTokenForDomain(domain)).access_token;
				if (!access_token)
					// return new Response(null, {
					// 	status: 401,
					// 	statusText: 'Do not have access token for this domain'
					// });
					return new Response(JSON.stringify({
						error: 'Не удалось получить access_token для этого домена',
						success: false
					}), {
						status: 401,
						statusText: 'Do not get access token',
						headers: {
							'Content-Type': 'application/json'
						}
					});
			}
		}
		// Если домен не указан
		else {
			url = path;
		}

		// Создаем объект для запроса
		let init: RequestInit = {
			method: method,
			credentials: 'include',
			cache: 'no-store',
			headers: {
				'X-Target': 'API'
			}
		}

		// Добавляем заголовок для авторизации, если есть токен
		if (access_token) {
			init.headers = {
				...init.headers,
				'Authorization': 'Bearer ' + access_token,
			}
		}

		// Добавляем заголовки запроса, если они заданы
		if (headers && typeof headers === 'object') {
			init.headers = {
				...init.headers,
				...headers
			}
		}

		// DELETE mutation APIs also carry revision and opening-context data.
		if (['POST', 'PUT', 'PATCH'].includes(method) || (method === 'DELETE' && body !== undefined)) {

			if (['POST', 'PUT', 'PATCH'].includes(method) && body === undefined)
				throw new Error('Параметр body должен быть задан для данного типа запроса');

			if (!(init.headers as Record<string, string>)['Content-Type'])
				(init.headers as Record<string, string>)['Content-Type'] = 'application/json';

			const content_type = (init.headers as Record<string, string>)['Content-Type']
			if ([
					'application/json',
					'application/x-www-form-urlencoded',
					'multipart/form-data'
				].includes(content_type)
				&& typeof body !== 'object'
			)
				throw new Error('Неправильный тип тела для данного типа запроса');

			switch (content_type) {
				case 'application/json':
					init.body = JSON.stringify(body);
					break
				case 'application/x-www-form-urlencoded':
					init.body = new URLSearchParams(body).toString();
					break
				case 'multipart/form-data':
					// Для multipart/form-data не нужно устанавливать Content-Type, браузер сам это сделает
					delete (init.headers as Record<string, string>)['Content-Type'];
					const formData = new FormData()
					for (const key in body) {
						formData.append(key, body[key]);
					}
					init.body = formData;
					break
				default:
					throw new Error('Не поддерживаемый тип тела запроса');
			}
		}

		// Выполняем запрос
		return await this.retryFetch(url, init)
	}

	private resolveRequestScheme(domain: string): string {
		if (
			typeof window !== 'undefined'
			&& window.location?.hostname === domain
			&& window.location?.protocol
		) {
			return window.location.protocol.replace(':', '') || 'https'
		}

		return 'https'
	}

	async getResponseJson(response: Response): Promise<ResponseData> {
		let result: ResponseData;
		const responseBody = await response.text();

		// Получаем ответ сервера в формате JSON
		try {
			result = responseBody.trim() !== ''
				? JSON.parse(responseBody) as ResponseData
				: {
					error: 'Ответ сервера пустой или не JSON',
					success: false
				} as ResponseData;
		} catch (error) {
			if (error instanceof SyntaxError) {
				result = {
					error: 'Неправильный response! Ответ сервера: ' +
						this.nonJsonResponsePreview(responseBody),
					success: false
				} as ResponseData;
			} else {
				throw error;
			}
		}

		// Если ответ пустой или не JSON
		if (!result || typeof result !== 'object') {
			result = {
				error: 'Ответ сервера пустой или не JSON',
				success: false
			} as ResponseData;
		}
		else {
			if (!('success' in result))
				(result as ResponseData).success = false;
		}

		// Добавляем код и текст ответа сервера
		if (typeof result.response_code !== 'number')
			result.response_code = response.status;
		if (typeof result.response_text !== 'string')
			result.response_text = response.statusText;

		// Если есть явная ошибка от сервера
		if (!response.ok || !result.success) {
			const status = Number.isInteger(response.status) && response.status >= 0
				? response.status
				: 0;
			console.warn('api_response_failure', status);

			// Добавляем текст ошибки, если его нет
			if (typeof result.error !== 'string')
				result.error = '(ответ сервера не содержит поля error или error не строка)';

			// Если код ошибки 500 или больше, добавляем инструкцию для пользователя
			if (response.status >= 500)
				result.error = result.error + '\nПожалуйста, попробуйте еще раз, либо обратитесь в нашу поддержку';

		}

		return result;
	}

	private nonJsonResponsePreview(responseBody: string): string {
		let preview = responseBody.trim();

		if (preview.startsWith('<')) {
			const title = preview.match(/<title[^>]*>([\s\S]*?)<\/title>/i)?.[1] || '';
			const bodyText = preview
				.replace(/<script[\s\S]*?<\/script>/gi, ' ')
				.replace(/<style[\s\S]*?<\/style>/gi, ' ')
				.replace(/<[^>]+>/g, ' ');
			preview = `${title} ${bodyText}`;
		}

		preview = this.decodeHtmlEntities(preview).replace(/\s+/g, ' ').trim();
		if (!preview)
			return '(не содержит JSON)';

		return preview.length > 1000
			? preview.slice(0, 1000) + '...'
			: preview;
	}

	private decodeHtmlEntities(value: string): string {
		if (typeof document === 'undefined')
			return value;

		const textarea = document.createElement('textarea');
		textarea.innerHTML = value;
		return textarea.value;
	}

	private async getAccessTokenForDomain(target_domain: string): Promise<{ access_token: string, expires_at: number }> {
		if (!this.haveConfig())
			throw new Error('Конфигурация приложения отсутствует')

		// Получаем правильный target_domain
		if (!!target_domain)
			target_domain = (new URL(
				target_domain.startsWith('http') ? target_domain : 'https://' + target_domain
			)).hostname;

		// Проверяем, является ли target_domain authDomain'ом
		if (target_domain === this.getConfig().auth_domain)
			throw new Error('Запрос токена для authDomain запрещен');

		// Проверяем, есть ли уже токен для этого домена и не истек ли он
		if (this.accessTokens[target_domain]) {
			const tokenData = this.accessTokens[target_domain]
			// Проверяем, нужно ли обновить токен (например, за 1 минуту до истечения)
			const refresh_threshold = (this.getConfig().jwt?.refresh_before || 60) * 1000
			if (tokenData.expires_at * 1000 > Date.now() + refresh_threshold)
				return tokenData;
		}

		// Если нет токена или его срок жизни истекает, запрашиваем его у authDomain
		const response = await this.doFetch(
			this.getConfig().auth_domain,
			'/main/auth/s/get-token',
			'GET',
			undefined,
			{ 'Target-Domain': target_domain }
		);

		const result = await this.getResponseJson(response);

		// Проверяем на ошибку сервера
		if (result.response_code && result.response_code >= 500)
			throw new Error(`Ошибка сервера: ${result.response_text}\n${result.error || ''}`);

		// Проверяем на успех
		if (result.success) {

			// Получаем параметры авторизации: expires_at, user_id
			this.getAuthData(result);

			// Получаем access_token
			const accessTokenData = result.data.token_data
			if (
				!accessTokenData
				|| typeof accessTokenData !== 'object'
				|| !accessTokenData.access_token
			)
				throw new Error('Ответ сервера не содержит access_token');

			if (typeof accessTokenData.access_token !== 'string')
				throw new Error('Ответ сервера не содержит access_token в ожидаемом формате');

			if (!this.getTokenExpiresAt(accessTokenData.expires_at))
				throw new Error('Не удалось получить "время жизни" access-токена');

			return this.accessTokens[target_domain] = accessTokenData;
		}

		// Если не success
		console.warn('api_access_token_unavailable');
		// throw new Error(result.error || 'Не удалось получить access_token');
		return { access_token: '', expires_at: 0 };
	}

	private async retryFetch(url: string, init: RequestInit, maxRetries = 3): Promise<Response> {

		const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
		let lastResponse: Response | null = null

		for (let attempt = 0; attempt < maxRetries; attempt++) {
			try {

				const response = await fetch(url, init);

				if (response.status < 500)
					return response;

				lastResponse = response;

				if (attempt < maxRetries - 1)
					await delay(1000);

			} catch (error) {
				console.warn('api_fetch_retry', attempt + 1);
				let error_message = (error instanceof Error) ? error.message : String(error);
				if (attempt === maxRetries - 1)
					throw new Error(`Ошибка при выполнении запроса fetch: ${error_message}`);
				await delay(1000);
			}
		}

		if (lastResponse)
			return lastResponse;

		throw new Error('Запрос не был выполнен');
	}

	async checkAuth(): Promise<boolean> {
		if (!this.haveConfig())
			await this.fetchConfig();

		// Если отсутствует "время окончания авторизации" или оно уже истекло -
		// проверяем пользователя на авторизацию (пробуем получить токен за счёт cookies)
		if (
			!this.authorization_expires_at
			|| this.authorization_expires_at < Date.now()
		) {

			// Проверяем авторизацию на сервере
			const response = await this.doFetch(
				this.getConfig().auth_domain,
				'/main/auth/s/check-auth'
			);

			const result = await this.getResponseJson(response);

			// Проверяем на ошибку сервера
			if (result.response_code && result.response_code >= 500)
				throw new Error(`Ошибка сервера: ${result.response_text}\n${result.error || ''}`);

			// Пользователь был авторизован ранее: получаем данные авторизации
			if (result.success) {
				this.getAuthData(result);
			}
			// Пользователь не был авторизован ранее: сбросим токен и остальные данные авторизации
			else {
				this.clearUserData();
			}
		}

		// Возвращаем результат проверки авторизации
		return !!(
			this.authorization_expires_at &&
			this.authorization_expires_at > Date.now()
		);
	}

	getAuthData(result: ResponseData): void {

		if (!result.data || typeof result.data !== 'object')
			throw new Error('Ответ сервера не содержит данных авторизации');

		const expires_at = this.getTokenExpiresAt(result.data.expires_at);
		const user_id = this.getUserId(result.data.user_id);

		this.authorization_expires_at = expires_at;
		this.user_id = user_id;

	}

	getTokenExpiresAt(expires_at?: string | number): number {

		if (!expires_at)
			throw new Error('Время жизни токена "пустое"');

		expires_at = Number(expires_at);
		if (!Number.isInteger(expires_at))
			throw new Error('Время жизни токена не корректное');

		expires_at *= 1000;
		if (expires_at < Date.now())
			throw new Error(
				`Токен, полученный с сервера, истек ${new Date(expires_at).toLocaleString('ru')}`
			);

		return expires_at;
	}

	getUserId(user_id?: string | number): string {

		if (!user_id)
			throw new Error('ID пользователя "пустое"');

		user_id = Number(user_id);
		if (!Number.isInteger(user_id))
			throw new Error('ID пользователя не корректное');

		return String(user_id);
	}

	clearUserData(): void {
		this.authorization_expires_at = 0;
		this.user_id = '';
		this.accessTokens = {};
	}

	async getAliasData(target_path: string) {
		if (!this.haveConfig())
			await this.fetchConfig();

		const response = await this.doFetch(
			this.getConfig().cp_domain,
			'/main/main/s/get-alias-data',
			'GET',
			'',
			{ 'Target-Path': target_path }
		);

		const result = await this.getResponseJson(response);

		// Проверяем на ошибку сервера
		if (result.response_code && result.response_code >= 500)
			throw new Error(`Ошибка сервера: ${result.response_text}\n${result.error || ''}`);

		return result;
	}

}
